import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, inject } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { finalize, first, map, shareReplay } from 'rxjs/operators';
import moment from 'moment';
import { Messages } from 'src/app/common/enums/messages';
import { ErrorCode } from 'src/app/common/error-codes/ErrorCode';
import { download } from 'src/app/common/utils/downloadFile';
import { getBase64 } from 'src/app/common/utils/getBase64';
import { CreateOrUpdateDelegationInvoiceRequest, InvoiceItem } from 'src/app/contracts/requests/create-or-update-delegation-invoice-request';
import { VatCalculationService } from 'src/app/core/vat/vat-calculation.service';
import { DelegationService } from 'src/app/data/delegation.service';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { DownloadService } from 'src/app/data/download.service';
import { DelegationInvoiceListDataSource } from 'src/app/delegations/delegation-invoice-list/delegation-invoice-list.datasource';
import { DictionaryItem } from 'src/app/models/dictionary-item';
import { DelegationInvoiceDto, DelegationInvoiceItemDto } from 'src/app/models/dtos/delegation-invoice-dto';
import { FileDto } from 'src/app/models/dtos/file-dto';
import { CurrencyEnum } from 'src/app/models/enums/currency-enum';
import { VatDictionaryItem } from 'src/app/models/vat-dictionary-item';
import { ConfirmDialogComponent } from 'src/app/shared/messages/confirm-dialog/confirm-dialog.component';
import { DocumentViewerService } from 'src/app/shared/services/document-viewer.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';

export interface InvoiceForm {
  delegationId: FormControl<number | null>;
  invoiceNumber: FormControl<string | null>;
  description: FormControl<string | null>;
  paymentTypeId: FormControl<number | null>;
  salesDate: FormControl<Date | null>;
  documentDate: FormControl<Date | null>;
  totalGrossValue: FormControl<number>;
  totalNetValue: FormControl<number>;
  totalVatValue: FormControl<number>;
  invoiceItems: FormArray<FormGroup<InvoiceItemForm>>;
}

export interface InvoiceItemForm {
  name: FormControl<string>;
  categoryId: FormControl<number>;
  currencyId: FormControl<CurrencyEnum>;
  netValue: FormControl<number>;
  vatId: FormControl<number>;
  vatValue: FormControl<number>;
  grossValue: FormControl<number>;
}

@Component({
  selector: 'app-delegation-add-invoice-form',
  templateUrl: './delegation-add-invoice-form.component.html',
  styleUrls: ['./delegation-add-invoice-form.component.scss'],
  standalone: false
})
export class DelegationAddInvoiceFormComponent implements OnDestroy {
  data = inject<{
    delegationId: number;
    record: DelegationInvoiceDto;
    isEdit: boolean;
  }>(MAT_DIALOG_DATA);
  private formBuilder = inject(FormBuilder);
  private dialogRef = inject<MatDialogRef<DelegationAddInvoiceFormComponent>>(MatDialogRef);
  private dictionaryService = inject(DictionaryService);
  private delegationService = inject(DelegationService);
  private spinner = inject(NgxSpinnerService);
  private snackbar = inject(SnackBarService);
  dataSource = inject(DelegationInvoiceListDataSource);
  private downloadService = inject(DownloadService);
  private documentViewer = inject(DocumentViewerService);
  private vatCalculationService = inject(VatCalculationService);
  private dialog = inject(MatDialog);

  formGroup: FormGroup<InvoiceForm>;

  listOfCurrencies$: Observable<DictionaryItem[]> = this.dictionaryService.getCurrencies()
  listOfDelegationInvoiceTypes$: Observable<DictionaryItem[]> = this.dictionaryService.getDelegationInvoiceItemTypes();
  listOfPaymentTypes$: Observable<DictionaryItem[]> = this.dictionaryService.getPaymentTypes();
  listOfValueAddedTaxes$: Observable<VatDictionaryItem[]> = this.dictionaryService.getValueAddedTaxes().pipe(shareReplay(1));

  private readonly unsubscribe$ = new Subject<void>();
  public isEditMode = false;

  readonly maxFileCount: number = 15;
  private files: FileDto[] = [];
  savedFiles: any[];
  public isFilesUploaded: boolean = false;

  private readonly defaultPage: number = 1;
  private readonly defaultPageSize: number = 10;
  private readonly defaultSortColumn: string = 'invoiceNumber';
  private readonly defaultSortDirection: string = 'desc';

  private readonly SALES_DATE_DAYS_BEFORE_DOCUMENT = 15;
  private readonly SALES_DATE_DAYS_AFTER_DOCUMENT = 30;

  private agreementStartDate: Date | null = null;
  private agreementEndDate: Date | null = null;

  get formValues() {
    return this.formGroup.value;
  }
  get invoiceItems() {
    return this.formGroup.controls.invoiceItems;
  }
  constructor() { }

  async ngOnInit(): Promise<void> {
    this.formGroup = this.buildFormGroup();

    this.formGroup.controls.documentDate.valueChanges.subscribe(() => {
      const salesDate = this.formGroup.controls.salesDate.value;
      if (salesDate && !this.isValidSalesDate(salesDate)) {
        this.formGroup.controls.salesDate.setValue(null);
      }
    });

    this.isEditMode = this.data.isEdit;
    if (!!this.data.record) {
      this.fetchDelegationInvoice();
    }

    await this.fetchAgreementDateRange();
    this.formGroup.controls.documentDate.updateValueAndValidity();
    this.formGroup.controls.salesDate.updateValueAndValidity();
  }

  private async fetchAgreementDateRange(): Promise<void> {
    const settlement = await firstValueFrom(this.delegationService.getDelegationSettlementById(null, this.data.delegationId));
    this.agreementStartDate = settlement.AgreementEmploymentDateFrom ? new Date(settlement.AgreementEmploymentDateFrom) : null;

    const effectiveEndDate = settlement.AgreementTerminationEndDate ?? settlement.AgreementEmploymentDateTo;
    this.agreementEndDate = effectiveEndDate ? new Date(effectiveEndDate) : null;
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  saveCost() {
    if (this.formGroup.invalid) return;

    if (!this.isFilesUploaded && (!this.savedFiles || this.savedFiles.length === 0)) {
      return;
    }

    this.spinner.show();
    const request = this.createRequest();
    const action$ = !request.DelegationCostId
      ? this.delegationService.createDelegationInvoiceRecord(this.data.delegationId, request)
      : this.delegationService.updateDelegationInvoiceRecord(this.data.delegationId, request);
    const message = !request.DelegationCostId ? Messages.SuccessfullyCreatedDelegationInvoiceRecord : Messages.SuccessfullyUpdatedDelegationInvoiceRecord;
    const maximumCountErrorMessage = ErrorCode.DelegationInvoicesReachedMaximumCount;

    action$
      .pipe(
        first(),
        finalize(() => this.spinner.hide())
      )
      .subscribe({
        next: _ => {
          this.snackbar.openSuccessSnackBar(message);
          this.dialogRef.close(true);
          this.fetchInvoices();
        },
        error: (err: HttpErrorResponse) => {
          if (err.error === maximumCountErrorMessage) {
            this.snackbar.openErrorSnackBar(maximumCountErrorMessage);
          }
        }
      });
  }

  close = () => this.dialogRef.close();

  public displayValue(value: DictionaryItem): string | undefined {
    return value?.Name;
  }

  private fetchDelegationInvoice() {
    this.spinner.show();

    this.delegationService
      .getDelegationCostInvoiceById(this.data.record.DelegationId, this.data.record.DelegationCostId)
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe(async (data) => {
        await this.patchFormGroupValues(data, this.isEditMode);
        this.savedFiles = data.Files;
      });
  }

  private buildFormGroup() {
    return this.formBuilder.group<InvoiceForm>(
      {
        delegationId: this.formBuilder.control({ value: null }),
        invoiceNumber: this.formBuilder.control(null, [Validators.required]),
        description: this.formBuilder.control('', [Validators.required]),
        paymentTypeId: this.formBuilder.control(null, Validators.required),
        salesDate: this.formBuilder.control(null, [Validators.required, this.salesDateValidator.bind(this)]),
        documentDate: this.formBuilder.control(null, [Validators.required, this.documentDateValidator.bind(this)]),
        totalGrossValue: this.formBuilder.control({ value: 0, disabled: true }),
        totalNetValue: this.formBuilder.control({ value: 0, disabled: true }),
        totalVatValue: this.formBuilder.control({ value: 0, disabled: true }),
        invoiceItems: this.formBuilder.array([this.createEmptyInvoiceItem()])
      }
    );
  }

  createEmptyInvoiceItem(): FormGroup<InvoiceItemForm> {
    const fg = this.formBuilder.group<InvoiceItemForm>({
      name: this.formBuilder.control('', Validators.required),
      categoryId: this.formBuilder.control(null, Validators.required),
      currencyId: this.formBuilder.control(CurrencyEnum.PLN, Validators.required),
      netValue: this.formBuilder.control(0, [Validators.required, Validators.min(0)]),
      vatId: this.formBuilder.control(null, [Validators.required]),
      vatValue: this.formBuilder.control({ value: 0, disabled: true }, [Validators.required, Validators.min(0)]),
      grossValue: this.formBuilder.control(0, [Validators.required, Validators.min(0)]),
    });

    fg.controls.netValue.valueChanges.subscribe(() => this.updateValuesByNet(fg));
    fg.controls.grossValue.valueChanges.subscribe(() => this.updateValuesByGross(fg));
    fg.controls.vatId.valueChanges.subscribe(() => this.updateValuesByNet(fg));

    return fg;
  }

  private async createInvoiceItem(initialValue?: Partial<DelegationInvoiceItemDto>): Promise<FormGroup<InvoiceItemForm>> {
    const fg = this.formBuilder.group<InvoiceItemForm>({
      name: this.formBuilder.control(initialValue?.Name ?? '', Validators.required),
      categoryId: this.formBuilder.control(initialValue?.DelegationInvoiceItemTypeId ?? null, Validators.required),
      currencyId: this.formBuilder.control(initialValue?.CurrencyId ?? CurrencyEnum.PLN, Validators.required),
      netValue: this.formBuilder.control(initialValue?.NetValue ?? 0, [Validators.required, Validators.min(0)]),
      vatId: this.formBuilder.control(initialValue?.VatId, [Validators.required]),
      vatValue: this.formBuilder.control({ value: initialValue?.VatValue ?? 0, disabled: true }),
      grossValue: this.formBuilder.control(initialValue?.GrossValue ?? 0, [Validators.required, Validators.min(0)]),
    });

    fg.controls.netValue.valueChanges.subscribe(() => this.updateValuesByNet(fg));
    fg.controls.grossValue.valueChanges.subscribe(() => this.updateValuesByGross(fg));
    fg.controls.vatId.valueChanges.subscribe(() => this.updateValuesByNet(fg));
    fg.controls.vatValue.valueChanges.subscribe(() => this.updateValuesByVatValue(fg));

    return fg;
  }
  private async findVatById(vatId: number): Promise<VatDictionaryItem> {
    return await firstValueFrom(this.listOfValueAddedTaxes$.pipe(map(list => list.find(item => item.Id === vatId))));
  }

  private async updateValuesByNet(fg: FormGroup<InvoiceItemForm>) {
    const vatId = fg.controls.vatId.value;

    if (!vatId) {
      return;
    }

    const net = fg.controls.netValue.value;
    const vat = await this.findVatById(vatId);

    const vatValue = this.vatCalculationService.calculateVatValue(net, vat.Rate);
    const gross = this.vatCalculationService.calculateGross(net, vat.Rate);

    fg.controls.vatValue.setValue(vatValue, { emitEvent: false });
    fg.controls.grossValue.setValue(gross, { emitEvent: false });
    this.updateTotals();
  }

  private async updateValuesByVatValue(fg: FormGroup<InvoiceItemForm>) {
    const vatValue = fg.controls.vatValue.value;
    const netValue = fg.controls.netValue.value;

    const gross = netValue + vatValue;

    fg.controls.grossValue.setValue(gross, { emitEvent: false });
    this.updateTotals();
  }

  private async updateValuesByGross(fg: FormGroup<InvoiceItemForm>): Promise<void> {
    const vatId = fg.controls.vatId.value;

    if (!vatId) {
      return;
    }

    const gross = fg.controls.grossValue.value;
    const vat = await this.findVatById(vatId);

    const net = this.vatCalculationService.calculateNet(gross, vat.Rate);
    const vatValue = this.vatCalculationService.calculateVatValue(net, vat.Rate);

    fg.controls.netValue.setValue(net, { emitEvent: false });
    fg.controls.vatValue.setValue(vatValue, { emitEvent: false });
    this.updateTotals();
  }

  private updateTotals(): void {
    const grossValue = this.invoiceItems.controls.sum(c => c.controls.grossValue.value);
    const netValue = this.invoiceItems.controls.sum(c => c.controls.netValue.value);
    const vatValue = this.invoiceItems.controls.sum(c => c.controls.vatValue.value);

    this.formGroup.patchValue({
      totalGrossValue: this.vatCalculationService.round(grossValue),
      totalNetValue: this.vatCalculationService.round(netValue),
      totalVatValue: this.vatCalculationService.round(vatValue)
    });

    this.formGroup.updateValueAndValidity();
  }

  addItem(): void {
    this.invoiceItems.push(this.createEmptyInvoiceItem());
  }

  async removeItem(index: number): Promise<void> {
    if (this.invoiceItems.length > 1 && await this.confirmRemoveItem()) {
      this.invoiceItems.removeAt(index);
    }
  }

  private async confirmRemoveItem(): Promise<boolean> {
    return await firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmRemoveDelegationInvoiceItemMessage } })
        .afterClosed());
  }

  private async patchFormGroupValues(record: DelegationInvoiceDto, isEdit: boolean): Promise<void> {
    this.formGroup.patchValue({
      delegationId: record.DelegationId,
      invoiceNumber: record.InvoiceNumber,
      description: record.Description,
      paymentTypeId: record.PaymentTypeId,
      salesDate: record.SalesDate ? new Date(record.SalesDate) : null,
      documentDate: record.DocumentDate ? new Date(record.DocumentDate) : null,
    });

    const itemsArray = this.formGroup.controls.invoiceItems;
    itemsArray.clear();

    for (const item of record.InvoiceItems) {
      const group = await this.createInvoiceItem(item);
      itemsArray.push(group);
    }

    this.updateTotals();

    this.formGroup.updateValueAndValidity();
    if (!isEdit) {
      this.formGroup.disable();
    }
  }

  private createRequest(): CreateOrUpdateDelegationInvoiceRequest {
    return {
      DelegationId: this.data.delegationId,
      DelegationCostId: this.data?.record?.DelegationCostId,
      InvoiceNumber: this.formGroup.value.invoiceNumber.toString().trim(),
      Description: this.formValues.description,
      PaymentTypeId: this.formValues.paymentTypeId,
      SalesDate: this.formValues.salesDate,
      DocumentDate: this.formValues.documentDate,
      Files: this.files,
      InvoiceItems: this.invoiceItems.controls.map(control => {
        const item = control.getRawValue();
        return {
          Name: item.name,
          DelegationInvoiceItemTypeId: item.categoryId,
          CurrencyId: item.currencyId,
          GrossValue: item.grossValue,
          NetValue: item.netValue,
          VatValue: item.vatValue,
          VatId: item.vatId
        } as InvoiceItem;
      })
    };
  }

  private documentDateValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    if (this.isDateInFuture(control.value)) {
      return { documentDateInFuture: true };
    }

    return this.isDateWithinAgreementRange(control.value) ? null : { documentDateOutsideAgreementRange: true };
  }

  private isDateInFuture(date: Date): boolean {
    return moment(date).startOf('day').isAfter(moment().startOf('day'), 'day');
  }

  private isDateWithinAgreementRange(date: Date): boolean {
    const selectedDate = moment(date).startOf('day');

    if (this.agreementStartDate && selectedDate.isBefore(moment(this.agreementStartDate), 'day')) {
      return false;
    }

    if (this.agreementEndDate && selectedDate.isAfter(moment(this.agreementEndDate), 'day')) {
      return false;
    }

    return true;
  }

  private salesDateValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const documentDate = this.formGroup.controls.documentDate.value;

    return this.isDateInValidSalesRange(control.value, documentDate) ? null : { invalidSalesDate: true };
  }

  onFilesChange(files: File[]): void {
    this.spinner.show();
    this.files = [];
    this.isFilesUploaded = !!files && files.length > 0;
    let counter = 0;

    files.forEach((file) => {
      getBase64(file, (base64) => {
        this.files.push({ OriginalName: file.name, FileContent: base64 });
        counter++;
      });
    });

    const interval = setInterval(() => {
      if (counter == files.length) {
        this.spinner.hide();
        clearInterval(interval);
      }
    }, 200);
  }

  isSaveButtonEnabled(): boolean {
    const hasFiles = this.isFilesUploaded || (this.savedFiles && this.savedFiles.length > 0);
    return this.formGroup.valid && hasFiles;
  }

  private fetchInvoices() {
    if (!this.data.delegationId) {
      return;
    }

    this.dataSource.delegationsInvoicesSubject.next({
      DelegationId: this.data.delegationId,
      Page: this.defaultPage,
      Count: this.defaultPageSize,
      SortingField: this.defaultSortColumn,
      SortingDirection: this.defaultSortDirection
    });
  }

  downloadCostFile(id: number, name: string): void {
    this.downloadService
      .getFileAsBlob(`delegations/delegationCost/${this.data?.record?.DelegationCostId}/delegationCostFile/${id}`, null, name)
      .pipe(first())
      .subscribe((srcUrl: string) => download(srcUrl, name));
  }

  viewCostFile = (fileId: number, fileName: string) =>
    this.documentViewer.show({
      Endpoint: `delegations/delegationCost/${this.data?.record?.DelegationCostId}/delegationCostFile`,
      FileId: fileId,
      FileName: fileName,
    });

  salesDateFilter = (date: Date | null): boolean => {
    if (!date) return true;

    const documentDate = this.formGroup.controls.documentDate.value;

    return this.isDateInValidSalesRange(date, documentDate);
  };

  documentDateFilter = (date: Date | null): boolean => {
    if (!date) return true;

    return !this.isDateInFuture(date) && this.isDateWithinAgreementRange(date);
  };

  private isValidSalesDate(salesDate: Date): boolean {
    if (!salesDate) {
      return true;
    }

    const documentDate = this.formGroup.controls.documentDate.value;

    return this.isDateInValidSalesRange(salesDate, documentDate);
  }

  private isDateInValidSalesRange(salesDate: Date, documentDate: Date | null): boolean {
    if (!this.isDateWithinAgreementRange(salesDate)) {
      return false;
    }

    if (!documentDate) {
      return true;
    }

    const selectedDate = moment(salesDate).startOf('day');
    const minDateFromDocument = moment(documentDate).subtract(this.SALES_DATE_DAYS_BEFORE_DOCUMENT, 'days');
    const maxDateFromDocument = moment(documentDate).add(this.SALES_DATE_DAYS_AFTER_DOCUMENT, 'days');

    return selectedDate.isSameOrAfter(minDateFromDocument, 'day') && selectedDate.isSameOrBefore(maxDateFromDocument, 'day');
  }
}
